<script setup lang="ts">
import { APP_TABS_DATA } from '~/constants/page-constants'
import type { AppDetailsSchema } from '~/types/product.model'

const route = useRoute()
const { origin, pathname } = useRequestURL()

defineProps<{ description: AppDetailsSchema['description'] }>()

const isAnchorActive = computed(() => {
  return {
    about: route.hash === `#${APP_TABS_DATA.overview.id}`,
    howToConnect: route.hash === `#${APP_TABS_DATA.howToConnect.id}`,
    resourcesAndSupport:
      route.hash === `#${APP_TABS_DATA.resourcesAndSupport.id}`,
    privacyAndTerms: route.hash === `#${APP_TABS_DATA.privacyAndTerms.id}`,
    carrier_links: route.hash === `#${APP_TABS_DATA.carrierLinks.id}`,
    reviews: route.hash === `#${APP_TABS_DATA.reviews.id}`,
  }
})

const trackLinkClick = useTrackLinkClick()

const trackTabClick = (tabsDataKey: keyof typeof APP_TABS_DATA): void => {
  trackLinkClick({
    pageLocation: 'Main Content',
    linkType: 'Tab',
    destinationTitle: APP_TABS_DATA[tabsDataKey].title,
    linkUrl: getAppDetailsTabUrl(APP_TABS_DATA[tabsDataKey].id, `${origin}${pathname}`),
  })
}
</script>

<template>
  <ClientOnly>
    <nav class="app-details-tabs">
      <ul class="app-details-tabs__list list-reset">
        <li v-if="description?.overview">
          <a
            class="app-details-tabs__anchor"
            :class="{ active: isAnchorActive.about }"
            :href="`#${APP_TABS_DATA.overview.id}`"
            @click="trackTabClick('overview')"
          >
            {{ APP_TABS_DATA.overview.title }}
          </a>
        </li>
        <li v-if="description?.how_to_install">
          <a
            class="app-details-tabs__anchor"
            :class="{ active: isAnchorActive.howToConnect }"
            :href="`#${APP_TABS_DATA.howToConnect.id}`"
            @click="trackTabClick('howToConnect')"
          >
            {{ APP_TABS_DATA.howToConnect.title }}
          </a>
        </li>
        <li v-if="description?.resources_support">
          <a
            class="app-details-tabs__anchor"
            :class="{ active: isAnchorActive.resourcesAndSupport }"
            :href="`#${APP_TABS_DATA.resourcesAndSupport.id}`"
            @click="trackTabClick('resourcesAndSupport')"
          >
            {{ APP_TABS_DATA.resourcesAndSupport.title }}
          </a>
        </li>
        <li v-if="description?.privacy_terms">
          <a
            class="app-details-tabs__anchor"
            :class="{ active: isAnchorActive.privacyAndTerms }"
            :href="`#${APP_TABS_DATA.privacyAndTerms.id}`"
            @click="trackTabClick('privacyAndTerms')"
          >
            {{ APP_TABS_DATA.privacyAndTerms.title }}
          </a>
        </li>
        <li v-if="description?.carrier_links">
          <a
            class="app-details-tabs__anchor"
            :class="{ active: isAnchorActive.carrier_links }"
            :href="`#${APP_TABS_DATA.carrierLinks.id}`"
            @click="trackTabClick('carrierLinks')"
          >
            {{ APP_TABS_DATA.carrierLinks.title }}
          </a>
        </li>
        <li>
          <a
            class="app-details-tabs__anchor"
            :class="{ active: isAnchorActive.reviews }"
            :href="`#${APP_TABS_DATA.reviews.id}`"
            @click="trackTabClick('reviews')"
          >
            {{ APP_TABS_DATA.reviews.title }}
          </a>
        </li>
      </ul>
    </nav>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.app-details-tabs {
  @include max-width('lg') {
    display: none;
  }
}

.app-details-tabs__list {
  display: inline-flex;
  gap: var(--app-spacer-32);
  border-bottom: 2px solid var(--app-color-border-line);
}

.app-details-tabs__anchor {
  position: relative;
  display: block;
  text-decoration: none;
  color: var(--app-text-color-primary);
  padding-bottom: var(--app-spacer-10);

  &.active {
    color: var(--app-color-link-hover);
    font-weight: 700;
  }

  &.active,
  &:hover {
    &::after {
      content: '';
      display: block;
      border-bottom: 2px solid var(--app-color-link-blue);
      position: absolute;
      bottom: -2px;
      width: 100%;
    }
  }
}
</style>
